import { ScrollArea } from '@/components/ui/scroll-area';

export default function DataValidationScroll() {
  return (
    <div className="">
      <h2>Troubleshooting</h2>
      <ScrollArea className="h-[800px] w-[1000px] rounded-md border p-4">
        <div className="font-mono space-y-4 text-sm leading-relaxed">
          <p>
            The following queries help identify any violations in the SAS BI data requirements for AWMS. These queries{' '}
            <strong>must return 0 rows</strong>. If they return results, it means there are duplicate or invalid records
            that need fixing in the Unit, Staff, or HDR Data to ensure the AWMS data load succeeds.
          </p>
          <div>
            <h3 className="font-semibold">0. Import flags</h3>
            <p>
              For unit and staff data, the import flag (import_yn) should be set to 'Y' for all records that need to be
              loaded into AWMS. If the import flag is 'N', the record won't be loaded into AWMS.
            </p>
          </div>
          <div>
            <h3 className="font-semibold">1. Unit Data - Mandatory fields</h3>
            <p>
              <strong>Requirement:</strong> <code>faculty</code>, <code>faculty_org_code</code>, <code>school</code>,{' '}
              <code>school_org_code</code>, and <code>year</code> must not be empty.
            </p>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT DISTINCT unit_code, year, study_period, campus, study_mode, faculty, faculty_org_code, school_org_code, school
        FROM unit_data
        WHERE faculty IS NULL OR faculty_org_code IS NULL OR year IS NULL OR school IS NULL OR school_org_code IS NULL;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">2. Staff Data - Mandatory fields</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT DISTINCT employee_no, school, school_org_code, faculty, faculty_org_code
        FROM staff_data
        WHERE faculty IS NULL OR faculty_org_code IS NULL OR year IS NULL OR school IS NULL OR school_org_code IS NULL;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">3. Staff Data - One School per Staff per Year</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT employee_no, year, COUNT(*)
        FROM (
          SELECT DISTINCT school_org_code, school, employee_no, year
          FROM staff_data
        ) 
        GROUP BY employee_no, year
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">4. Staff Data - Unique Staff Record per Year</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT employee_no, year, COUNT(*)
        FROM (
          SELECT DISTINCT employee_no, year, first_name, surname, school, discipline, emp_status, emp_status_desc,
                      work_function_desc, classification, fte, school_org_code, faculty, faculty_org_code, work_function_abbr, aqf
          FROM staff_data
        )
        GROUP BY employee_no, year
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">5. Unit Data - Mandatory Fields</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT DISTINCT unit_code, year, study_period, campus, study_mode, faculty, faculty_org_code, school_org_code, school
        FROM unit_data
        WHERE campus IS NULL OR study_mode IS NULL OR study_period IS NULL;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">6. Unit Data - One School per Unit per Year</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT COUNT(*), year, unit_code
        FROM (
          SELECT DISTINCT year, unit_code, school, unit_name
          FROM unit_data
        )
        GROUP BY year, unit_code
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">7. Unit Data - One Coordinator per Unit/Period</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT unit_code, year, study_period
        FROM (
          SELECT DISTINCT unit_code, year, study_period, unit_coordinator_employee_no,
                      unit_coordinator_surname, unit_coordinator_first_name, unit_coordinator_emp_status
          FROM unit_data
        )
        GROUP BY unit_code, year, study_period
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">8. Unit Data - Quota/Enrollment Consistency</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT unit_code, year, study_period, campus, study_mode
        FROM (
          SELECT DISTINCT unit_code, year, study_period, campus, max_enrollment_quota,
                      previous_year_enrollments, taught_weeks, study_mode
          FROM unit_data
        )
        GROUP BY unit_code, year, study_period, campus, study_mode
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">9. Unit Data - Activity Detail Consistency</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT COUNT(*), unit_code, year, study_period, campus, study_mode, activity_type
        FROM (
          SELECT DISTINCT unit_code, year, study_period, campus, study_mode, activity_type,
                      activity_duration, max_class_size, duration_measure
          FROM unit_data
        )
        GROUP BY unit_code, year, study_period, campus, study_mode, activity_type
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">10. HDR Data - Unique Student-Supervisor Record</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT student_id, unit_code, employee_no, year
        FROM (
          SELECT DISTINCT student_id, unit_code, employee_no, student_first_name, student_sur_name,
                      combined_eftsl, enrolment_stage, enrolment_status, supervisor_desc,
                      super_load_percent, year
          FROM hdr_student_data
        )
        GROUP BY student_id, unit_code, employee_no, year
        HAVING COUNT(*) > 1;`}
            </pre>
          </div>
          <div>
            <h3 className="font-semibold">11. HDR Data - Mandatory Fields</h3>
            <pre className="whitespace-pre-wrap rounded bg-muted p-2">
              {`SELECT DISTINCT student_id, unit_code, employee_no, student_first_name, student_sur_name,
                    combined_eftsl, enrolment_stage, enrolment_status, supervisor_desc,
                    super_load_percent, year
        FROM hdr_student_data
        WHERE student_id IS NULL OR employee_no IS NULL OR unit_code IS NULL;`}
            </pre>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
